import React, { createContext, useContext, useState, useEffect } from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession((err, session) => {
        if (err || !session.isValid()) {
          setUser(null);
          setLoading(false);
        } else {
          cognitoUser.getUserAttributes((attrErr, attrs) => {
            if (!attrErr) {
              const attrsMap = {};
              attrs.forEach(a => { attrsMap[a.getName()] = a.getValue(); });
              setUser({
                username: cognitoUser.getUsername(),
                email: attrsMap["email"],
                role: attrsMap["custom:role"] || "client",
                session,
                cognitoUser,
              });
            }
            // setLoading(false) is here — inside getUserAttributes callback
            // so loading stays true until user is fully populated
            setLoading(false);
          });
        }
      });
    } else {
      setLoading(false);
    }
  }, []);

  const signIn = (email, password) =>
    new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: session => {
          cognitoUser.getUserAttributes((err, attrs) => {
            const attrsMap = {};
            if (!err) attrs.forEach(a => { attrsMap[a.getName()] = a.getValue(); });
            const u = {
              username: cognitoUser.getUsername(),
              email: attrsMap["email"] || email,
              role: attrsMap["custom:role"] || "client",
              session,
              cognitoUser,
            };
            setUser(u);
            resolve(u);
          });
        },
        onFailure: err => reject(err),
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          resolve({ newPasswordRequired: true, cognitoUser, userAttributes });
        },
      });
    });

  const signOut = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    setUser(null);
  };

  const resetPassword = (email) =>
    new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.forgotPassword({ onSuccess: resolve, onFailure: reject });
    });

  const confirmResetPassword = (email, code, newPassword) =>
    new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: resolve,
        onFailure: reject,
      });
    });

  const completeNewPassword = (cognitoUser, newPassword) =>
    new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: session => {
          cognitoUser.getUserAttributes((err, attrs) => {
            const attrsMap = {};
            if (!err) attrs.forEach(a => { attrsMap[a.getName()] = a.getValue(); });
            const u = {
              username: cognitoUser.getUsername(),
              email: attrsMap["email"],
              role: attrsMap["custom:role"] || "client",
              session,
              cognitoUser,
            };
            setUser(u);
            resolve(u);
          });
        },
        onFailure: reject,
      });
    });

  return (
    <AuthContext.Provider
      value={{ user, loading, signIn, signOut, resetPassword, confirmResetPassword, completeNewPassword }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
